/**
 * Offline API Queue Manager
 * Handles storing API calls when offline or when backend is unavailable and syncing when online
 */

const OFFLINE_QUEUE_KEY = 'frappe_offline_api_queue';
const MAX_QUEUE_SIZE = 100;
const API_TIMEOUT = 10000; // 10 second timeout for API calls

export class OfflineApiQueue {
  constructor() {
    this.isOnline = navigator.onLine;
    this.isBackendAvailable = true;
    this.processingQueue = false;
    
    console.log('🔧 [OfflineQueue] Initializing with state:', {
      isOnline: this.isOnline,
      isBackendAvailable: this.isBackendAvailable,
      queueSize: this.getQueueSize()
    });
    
    this.setupEventListeners();
    
    // Check if there are queued items on initialization
    // We'll try to process them after a short delay to allow backend health check to complete
    setTimeout(() => {
      const queueSize = this.getQueueSize();
      if (queueSize > 0 && this.isOnline && this.isBackendAvailable) {
        console.log(`🔧 [OfflineQueue] Found ${queueSize} queued items on initialization, attempting to process...`);
        this.processQueue();
      }
    }, 2000); // 2 second delay to allow health check
  }

  setupEventListeners() {
    // Listen for online/offline events (network level)
    window.addEventListener('online', () => {
      console.log('📶 [OfflineQueue] Network back online');
      this.isOnline = true;
      console.log(`📶 [OfflineQueue] State updated - isOnline: ${this.isOnline}, isBackendAvailable: ${this.isBackendAvailable}`);
      // Don't process queue yet - wait for backend-available event
    });

    window.addEventListener('offline', () => {
      console.log('📵 [OfflineQueue] Network offline');
      this.isOnline = false;
      this.isBackendAvailable = false;
      console.log(`📵 [OfflineQueue] State updated - isOnline: ${this.isOnline}, isBackendAvailable: ${this.isBackendAvailable}`);
    });

    // Listen for backend availability events (application level)
    window.addEventListener('backend-available', () => {
      console.log('✅ [OfflineQueue] Backend-available event received');
      this.isBackendAvailable = true;
      const queueSize = this.getQueueSize();
      console.log(`✅ [OfflineQueue] State updated - isOnline: ${this.isOnline}, isBackendAvailable: ${this.isBackendAvailable}, Queue size: ${queueSize}`);
      
      if (queueSize > 0) {
        console.log(`✅ [OfflineQueue] Processing ${queueSize} queued API calls...`);
        this.processQueue();
      } else {
        console.log('✅ [OfflineQueue] No queued API calls to process');
      }
    });

    window.addEventListener('backend-unavailable', () => {
      console.log('⚠️ [OfflineQueue] Backend-unavailable event received');
      this.isBackendAvailable = false;
      console.log(`⚠️ [OfflineQueue] State updated - isOnline: ${this.isOnline}, isBackendAvailable: ${this.isBackendAvailable}`);
    });

    // Listen for service worker sync events
    window.addEventListener('sw-api-sync', () => {
      console.log('🔄 [OfflineQueue] Service worker sync event received');
      console.log(`🔄 [OfflineQueue] Current state - isOnline: ${this.isOnline}, isBackendAvailable: ${this.isBackendAvailable}`);
      if (this.isOnline && this.isBackendAvailable) {
        this.processQueue();
      } else {
        console.log('🔄 [OfflineQueue] Not processing - conditions not met');
      }
    });
  }

  /**
   * Add an API call to the offline queue
   */
  queueApiCall(type, data) {
    const queue = this.getQueue();
    
    if (queue.length >= MAX_QUEUE_SIZE) {
      console.warn('⚠️ [OfflineQueue] Queue is full, removing oldest entries');
      queue.splice(0, queue.length - MAX_QUEUE_SIZE + 1);
    }

    const queueItem = {
      id: Date.now() + Math.random(),
      type,
      data,
      timestamp: new Date().toISOString(),
      retryCount: 0
    };

    queue.push(queueItem);
    this.saveQueue(queue);
    
    console.log(`� [OfflineQueue] Queued ${type} API call (queue size: ${queue.length}):`, {
      id: queueItem.id,
      type: queueItem.type,
      timestamp: queueItem.timestamp,
      currentState: {
        isOnline: this.isOnline,
        isBackendAvailable: this.isBackendAvailable
      }
    });
    
    return queueItem.id;
  }

  /**
   * Get the current queue from localStorage
   */
  getQueue() {
    try {
      const stored = localStorage.getItem(OFFLINE_QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading offline queue:', error);
      return [];
    }
  }

  /**
   * Save the queue to localStorage
   */
  saveQueue(queue) {
    try {
      localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('Error saving offline queue:', error);
    }
  }

  /**
   * Process all queued API calls when online and backend is available
   */
  async processQueue() {
    console.log(`📤 [OfflineQueue] processQueue() called - isOnline: ${this.isOnline}, isBackendAvailable: ${this.isBackendAvailable}, processingQueue: ${this.processingQueue}`);
    
    if (!this.isOnline || !this.isBackendAvailable) {
      console.log('📤 [OfflineQueue] Cannot process queue - not ready (offline or backend unavailable)');
      return;
    }
    
    if (this.processingQueue) {
      console.log('📤 [OfflineQueue] Already processing queue, skipping...');
      return;
    }

    this.processingQueue = true;
    const queue = this.getQueue();

    if (queue.length === 0) {
      console.log('📤 [OfflineQueue] Queue is empty, nothing to process');
      this.processingQueue = false;
      return;
    }

    console.log(`📤 [OfflineQueue] Starting to process ${queue.length} queued API calls`);
    console.log('📤 [OfflineQueue] Queue items:', queue.map(item => ({ type: item.type, timestamp: item.timestamp, retryCount: item.retryCount })));

    const failedCalls = [];

    for (const item of queue) {
      console.log(`📤 [OfflineQueue] Processing item: ${item.type} (retry: ${item.retryCount || 0})`);
      try {
        const result = await this.executeApiCall(item);
        
        // Check if API call actually succeeded
        if (result && result.success) {
          console.log(`✅ [OfflineQueue] Successfully processed queued ${item.type} call`);
        } else {
          // API call returned but with an error
          console.error(`❌ [OfflineQueue] API call failed for ${item.type}:`, result);
          
          // Show detailed error alert to user
          this.showQueuedApiError(item, result);
          
          // Don't retry these errors - they're likely data validation errors
          console.warn(`⚠️ [OfflineQueue] Dropping ${item.type} call due to API error:`, item);
        }
      } catch (error) {
        console.error(`❌ [OfflineQueue] Failed to process queued ${item.type} call:`, error);
        
        // Check if it's a network/backend error vs other errors
        const isNetworkError = error.message.includes('fetch') || 
                               error.message.includes('network') ||
                               error.message.includes('timeout');
        
        item.retryCount = (item.retryCount || 0) + 1;
        
        // Keep items that haven't exceeded retry limit
        if (item.retryCount < 3) {
          console.log(`⚠️ [OfflineQueue] Will retry ${item.type} call (attempt ${item.retryCount}/3)`);
          failedCalls.push(item);
        } else {
          console.warn(`⚠️ [OfflineQueue] Dropping ${item.type} call after 3 retries:`, item);
          // Show error to user for final failed attempt
          this.showQueuedApiError(item, { error: error.message, statusCode: 'Network Error' });
        }

        // If it's a network error, mark backend as unavailable and stop processing
        if (isNetworkError) {
          this.isBackendAvailable = false;
          console.warn('⚠️ [OfflineQueue] Backend appears unavailable during processing, stopping queue processing');
          // Add remaining items back to failed calls
          const remainingIndex = queue.indexOf(item) + 1;
          failedCalls.push(...queue.slice(remainingIndex));
          break;
        }
      }
    }

    // Update queue with only failed calls that can be retried
    this.saveQueue(failedCalls);
    this.processingQueue = false;

    if (failedCalls.length > 0) {
      console.log(`📤 [OfflineQueue] ${failedCalls.length} API calls remain in queue for retry`);
    } else {
      console.log('✅ [OfflineQueue] All queued API calls processed successfully! Queue is now empty.');
    }
  }

  /**
   * Execute a queued API call
   */
  async executeApiCall(item) {
    const { type, data } = item;

    switch (type) {
      case 'insertOutputData':
        const { insertOutputData } = await import('./api');
        return await insertOutputData(
          data.salesOrder,
          data.item,
          data.colour,
          data.fit,
          data.size,
          data.postingDatetime,
          data.repaired
        );

      case 'insertDefectData':
        const { insertDefectData } = await import('./api');
        return await insertDefectData(
          data.salesOrder,
          data.item,
          data.colour,
          data.fit,
          data.size,
          data.outputQty,
          data.repairable,
          data.defectIds
        );

      default:
        throw new Error(`Unknown API call type: ${type}`);
    }
  }

  /**
   * Show detailed error alert for failed queued API calls
   */
  showQueuedApiError(item, result) {
    const { type, data, timestamp } = item;
    
    // Format the API endpoint name
    let apiName = '';
    if (type === 'insertOutputData') {
      apiName = 'Output/Pass API';
    } else if (type === 'insertDefectData') {
      apiName = 'Defect API';
    } else {
      apiName = type;
    }
    
    // Get error message
    const errorMessage = result?.error || result?.message || 'Unknown error';
    const statusCode = result?.statusCode || 'Unknown';
    
    // Create error object for the modal
    const errorDetails = {
      apiName,
      statusCode,
      timestamp: new Date(timestamp).toLocaleString(),
      errorMessage,
      parameters: data,
      isQueued: true
    };
    
    // Dispatch custom event to show error modal
    window.dispatchEvent(new CustomEvent('show-api-error', { 
      detail: errorDetails 
    }));
    
    // Also log to console for debugging
    console.error('📋 [OfflineQueue] Error Details:', {
      api: apiName,
      statusCode,
      timestamp,
      error: errorMessage,
      parameters: data
    });
  }

  /**
   * Get the current queue size
   */
  getQueueSize() {
    return this.getQueue().length;
  }

  /**
   * Clear the queue (for testing or manual intervention)
   */
  clearQueue() {
    localStorage.removeItem(OFFLINE_QUEUE_KEY);
    console.log('🗑️ [OfflineQueue] Queue cleared');
  }
  
  /**
   * Get current state for debugging
   */
  getStatus() {
    const status = {
      isOnline: this.isOnline,
      isBackendAvailable: this.isBackendAvailable,
      processingQueue: this.processingQueue,
      queueSize: this.getQueueSize(),
      queue: this.getQueue()
    };
    console.log('📊 [OfflineQueue] Current Status:', status);
    return status;
  }
  
  /**
   * Force process queue (for debugging)
   */
  forceProcessQueue() {
    console.log('🔧 [OfflineQueue] Force processing queue...');
    const originalState = {
      isOnline: this.isOnline,
      isBackendAvailable: this.isBackendAvailable
    };
    
    // Temporarily set to online/available
    this.isOnline = true;
    this.isBackendAvailable = true;
    
    console.log('🔧 [OfflineQueue] Temporarily set state to online/available for force processing');
    
    this.processQueue().finally(() => {
      // Restore original state
      this.isOnline = originalState.isOnline;
      this.isBackendAvailable = originalState.isBackendAvailable;
      console.log('🔧 [OfflineQueue] Restored original state');
    });
  }

  /**
   * Check if we should use offline mode for API calls
   * Returns true if network is offline OR backend is unavailable
   */
  shouldUseOfflineMode() {
    return !this.isOnline || !this.isBackendAvailable;
  }

  /**
   * Update backend availability status (can be called by API utilities)
   */
  setBackendAvailable(available) {
    if (this.isBackendAvailable !== available) {
      this.isBackendAvailable = available;
      console.log(`🔄 Backend availability updated: ${available ? 'Available' : 'Unavailable'}`);
      
      if (available) {
        window.dispatchEvent(new Event('backend-available'));
      } else {
        window.dispatchEvent(new Event('backend-unavailable'));
      }
    }
  }
}

// Create a singleton instance
export const offlineApiQueue = new OfflineApiQueue();

// Make it accessible from browser console for debugging
if (typeof window !== 'undefined') {
  window.offlineApiQueue = offlineApiQueue;
  console.log('🔧 [OfflineQueue] Queue manager accessible via window.offlineApiQueue');
  console.log('🔧 [OfflineQueue] Debug commands:');
  console.log('   - window.offlineApiQueue.getStatus() - View current status');
  console.log('   - window.offlineApiQueue.forceProcessQueue() - Force process queue');
  console.log('   - window.offlineApiQueue.clearQueue() - Clear all queued items');
}